import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const STATUS_LABEL = {
  boarded:       { label: 'Boarded',       bg: 'bg-green-100',  text: 'text-green-800',  icon: '✅' },
  dropped:       { label: 'Dropped',       bg: 'bg-blue-100',   text: 'text-blue-800',   icon: '🏠' },
  missed_pickup: { label: 'Missed Pickup', bg: 'bg-orange-100', text: 'text-orange-800', icon: '⚠️' },
  missed_drop:   { label: 'Missed Drop',   bg: 'bg-red-100',    text: 'text-red-800',    icon: '🚨' },
  absent:        { label: 'Absent',        bg: 'bg-slate-100',  text: 'text-slate-600',  icon: '—' },
};

function StatusBadge({ status }) {
  const s = STATUS_LABEL[status] || { label: status || 'Unknown', bg: 'bg-slate-100', text: 'text-slate-600', icon: '—' };
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold ${s.bg} ${s.text}`}>
      <span>{s.icon}</span>{s.label}
    </span>
  );
}

function Card({ children, className = '' }) {
  return <div className={`bg-white rounded-2xl shadow-sm border border-slate-100 p-5 ${className}`}>{children}</div>;
}

export default function TransportParentPortal() {
  const { token } = useParams();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/transport-parent/${token}`);
      setData(res.data);
      setLastRefresh(new Date());
      setError(null);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load data. Please check your link.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 120_000); // auto-refresh every 2 min
    return () => clearInterval(t);
  }, [load]);

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-100 flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-slate-600 text-sm">Loading transport status…</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-100 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 max-w-sm w-full text-center">
        <div className="text-4xl mb-4">⚠️</div>
        <h2 className="font-bold text-slate-800 text-lg mb-2">Link Error</h2>
        <p className="text-slate-500 text-sm">{error}</p>
        <p className="text-slate-400 text-xs mt-4">Please contact the school for a new link.</p>
      </div>
    </div>
  );

  if (!data) return null;

  const { student, mapping, today, live_position, history, notifications, school } = data;

  const boarding = today.attendance?.find(a => a.attendance_type === 'boarding');
  const drop     = today.attendance?.find(a => a.attendance_type === 'drop');
  const vStatus  = today.vehicle_status;

  // Group history by date
  const histByDate = {};
  (history || []).forEach(h => {
    if (!histByDate[h.attendance_date]) histByDate[h.attendance_date] = {};
    histByDate[h.attendance_date][h.attendance_type] = h;
  });

  const fmtTime = dt => dt
    ? new Date(dt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })
    : null;

  const fmtDate = d => new Date(d + 'T00:00').toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-700 to-blue-600 text-white px-5 py-6 shadow-lg">
        <div className="max-w-md mx-auto">
          <p className="text-blue-200 text-xs font-medium uppercase tracking-wide mb-1">{school.name}</p>
          <h1 className="text-xl font-bold">{student.name}</h1>
          <p className="text-blue-200 text-sm mt-0.5">Class {student.class_name} {student.section} · {student.admission_no}</p>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-5 space-y-4">

        {/* Today's Status */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs text-slate-500 uppercase font-medium">Today's Status</p>
              <p className="text-sm text-slate-700 font-medium mt-0.5">
                {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
              </p>
            </div>
            {lastRefresh && (
              <p className="text-xs text-slate-400">Updated {lastRefresh.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Morning */}
            <div className="bg-slate-50 rounded-xl p-4 text-center">
              <p className="text-xs text-slate-500 mb-2">🌅 Morning Pickup</p>
              {boarding
                ? <>
                    <StatusBadge status={boarding.status} />
                    {boarding.event_time && <p className="text-xs text-slate-500 mt-2">{fmtTime(boarding.event_time)}</p>}
                  </>
                : <div>
                    <p className="text-slate-400 text-sm">Not yet</p>
                    {mapping?.expected_pickup && <p className="text-xs text-blue-600 mt-1">Expected {mapping.expected_pickup}</p>}
                  </div>
              }
            </div>

            {/* Afternoon */}
            <div className="bg-slate-50 rounded-xl p-4 text-center">
              <p className="text-xs text-slate-500 mb-2">🌆 Afternoon Drop</p>
              {drop
                ? <>
                    <StatusBadge status={drop.status} />
                    {drop.event_time && <p className="text-xs text-slate-500 mt-2">{fmtTime(drop.event_time)}</p>}
                  </>
                : <div>
                    <p className="text-slate-400 text-sm">Pending</p>
                    {mapping?.expected_drop && <p className="text-xs text-blue-600 mt-1">Expected {mapping.expected_drop}</p>}
                  </div>
              }
            </div>
          </div>

          {/* Vehicle status */}
          {vStatus && (
            <div className="mt-3 flex items-center gap-2 text-xs text-slate-500 bg-blue-50 rounded-lg px-3 py-2">
              <span>🚌</span>
              <span>Bus status: <b className="text-blue-700">{vStatus.status?.replace(/_/g, ' ')}</b></span>
              {vStatus.departure_time && <span>· Departed {fmtTime(vStatus.departure_time)}</span>}
            </div>
          )}
        </Card>

        {/* Live GPS */}
        {live_position && live_position.latitude && (
          <Card>
            <p className="text-xs text-slate-500 uppercase font-medium mb-3">🗺 Live Bus Location</p>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-700">
                  <span className={`inline-block w-2 h-2 rounded-full mr-2 ${live_position.minutes_ago <= 5 ? 'bg-green-500' : live_position.minutes_ago <= 15 ? 'bg-amber-500' : 'bg-red-400'}`} />
                  {live_position.minutes_ago <= 0 ? 'Just now' : `${live_position.minutes_ago} min ago`}
                </p>
                {live_position.speed != null && (
                  <p className="text-xs text-slate-500 mt-1">Speed: {parseFloat(live_position.speed).toFixed(0)} km/h</p>
                )}
              </div>
              <a
                href={`https://maps.google.com/?q=${live_position.latitude},${live_position.longitude}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg"
              >
                📍 Open Map
              </a>
            </div>
          </Card>
        )}

        {/* Route Info */}
        {mapping && (
          <Card>
            <p className="text-xs text-slate-500 uppercase font-medium mb-3">🚌 Route & Vehicle</p>
            <div className="space-y-2.5 text-sm">
              {mapping.route_name && (
                <div className="flex justify-between">
                  <span className="text-slate-500">Route</span>
                  <span className="font-medium text-slate-800">{mapping.route_code} – {mapping.route_name}</span>
                </div>
              )}
              {mapping.vehicle_number && (
                <div className="flex justify-between">
                  <span className="text-slate-500">Bus Number</span>
                  <span className="font-mono font-bold text-blue-700">{mapping.vehicle_number}</span>
                </div>
              )}
              {mapping.driver_name && (
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">Driver</span>
                  <div className="text-right">
                    <span className="font-medium text-slate-800">{mapping.driver_name}</span>
                    {mapping.driver_phone && (
                      <a href={`tel:${mapping.driver_phone}`} className="block text-xs text-blue-600">
                        📞 {mapping.driver_phone}
                      </a>
                    )}
                  </div>
                </div>
              )}
              {mapping.pickup_stop && (
                <div className="flex justify-between">
                  <span className="text-slate-500">Pickup Stop</span>
                  <div className="text-right">
                    <span className="font-medium text-slate-800">{mapping.pickup_stop}</span>
                    {mapping.expected_pickup && <p className="text-xs text-blue-600">{mapping.expected_pickup}</p>}
                  </div>
                </div>
              )}
              {mapping.drop_stop && (
                <div className="flex justify-between">
                  <span className="text-slate-500">Drop Stop</span>
                  <span className="font-medium text-slate-800">{mapping.drop_stop}</span>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Last 7 days history */}
        {Object.keys(histByDate).length > 0 && (
          <Card>
            <p className="text-xs text-slate-500 uppercase font-medium mb-3">📅 Last 7 Days</p>
            <div className="space-y-2">
              {Object.entries(histByDate).sort(([a],[b]) => b.localeCompare(a)).map(([date, recs]) => (
                <div key={date} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
                  <span className="text-sm text-slate-600">{fmtDate(date)}</span>
                  <div className="flex gap-2">
                    {recs.boarding
                      ? <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_LABEL[recs.boarding.status]?.bg || 'bg-slate-100'} ${STATUS_LABEL[recs.boarding.status]?.text || 'text-slate-600'}`}>
                          {STATUS_LABEL[recs.boarding.status]?.icon} AM
                        </span>
                      : <span className="text-xs px-2 py-0.5 rounded-full bg-slate-50 text-slate-400">— AM</span>
                    }
                    {recs.drop
                      ? <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_LABEL[recs.drop.status]?.bg || 'bg-slate-100'} ${STATUS_LABEL[recs.drop.status]?.text || 'text-slate-600'}`}>
                          {STATUS_LABEL[recs.drop.status]?.icon} PM
                        </span>
                      : <span className="text-xs px-2 py-0.5 rounded-full bg-slate-50 text-slate-400">— PM</span>
                    }
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Recent notifications */}
        {notifications?.length > 0 && (
          <Card>
            <p className="text-xs text-slate-500 uppercase font-medium mb-3">🔔 Recent Notifications</p>
            <div className="space-y-2">
              {notifications.slice(0, 5).map((n, i) => (
                <div key={i} className="text-xs text-slate-600 bg-slate-50 rounded-lg px-3 py-2">
                  <p>{n.message}</p>
                  <p className="text-slate-400 mt-1">{n.created_at ? new Date(n.created_at).toLocaleString('en-IN') : ''}</p>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* School contact */}
        <Card>
          <p className="text-xs text-slate-500 uppercase font-medium mb-3">📞 School Contact</p>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-slate-800">{school.name}</p>
              <p className="text-xs text-slate-500 mt-0.5">Transport Department</p>
            </div>
            <a href={`tel:${school.phone}`}
              className="flex items-center gap-2 bg-blue-600 text-white text-sm px-4 py-2 rounded-xl font-medium">
              📞 Call
            </a>
          </div>
        </Card>

        <p className="text-center text-xs text-slate-400 pb-6">
          Auto-refreshes every 2 minutes · {lastRefresh?.toLocaleTimeString('en-IN')}
        </p>
      </div>
    </div>
  );
}
